import { Track } from "@/src/lib/tracking";
import { Subscription, SubscriptionCallback, SubscribeOptions } from "./types";

/**
 * 订阅管理器
 * 负责管理所有的主题订阅和回调
 */
export class SubscriptionManager {
  private subscriptions: Map<string, Subscription> = new Map();
  private sendCallback: ((topic: string) => void) | null = null;
  private debug: boolean = false;

  constructor(debug: boolean = false) {
    this.debug = debug;
  }

  /**
   * 设置发送订阅消息的回调
   */
  setSendCallback(callback: (topic: string) => void): void {
    this.sendCallback = callback;
  }

  /**
   * 订阅主题
   */
  subscribe(
    topic: string,
    callback: SubscriptionCallback,
    options: SubscribeOptions = {},
  ): () => void {
    const { immediate = true } = options;

    if (!topic) {
      Track.log("error", "SubscriptionManager", "主题不能为空");
      return () => {};
    }

    // 获取或创建订阅
    let subscription = this.subscriptions.get(topic);

    if (!subscription) {
      subscription = {
        topic,
        callbacks: new Set(),
        subscribedAt: Date.now(),
        sent: false,
      };
      this.subscriptions.set(topic, subscription);

      if (this.debug) {
      }
    }

    // 添加回调
    subscription.callbacks.add(callback);

    if (this.debug) {
    }

    // 如果需要立即发送订阅请求且尚未发送
    if (immediate && !subscription.sent && this.sendCallback) {
      try {
        this.sendCallback(topic);
        subscription.sent = true;

        if (options.onSuccess) {
          options.onSuccess();
        }

        if (this.debug) {
        }
      } catch (error) {
        Track.log(
          "error",
          "SubscriptionManager",
          `发送订阅请求失败: ${topic}`,
          { cause: String(error) },
        );

        if (options.onError) {
          options.onError(error as Error);
        }
      }
    }

    // 返回取消订阅的函数
    return () => {
      this.unsubscribe(topic, callback);
    };
  }

  /**
   * 取消订阅
   */
  unsubscribe(topic: string, callback?: SubscriptionCallback): void {
    const subscription = this.subscriptions.get(topic);

    if (!subscription) {
      if (this.debug) {
      }
      return;
    }

    if (callback) {
      // 删除特定回调
      subscription.callbacks.delete(callback);

      if (this.debug) {
      }

      // 如果没有剩余回调,删除整个订阅
      if (subscription.callbacks.size === 0) {
        this.subscriptions.delete(topic);

        if (this.debug) {
        }
      }
    } else {
      // 删除整个主题的所有回调
      this.subscriptions.delete(topic);

      if (this.debug) {
      }
    }
  }

  /**
   * 取消所有订阅
   */
  unsubscribeAll(): void {
    if (this.debug) {
    }

    this.subscriptions.clear();
  }

  /**
   * 分发消息到订阅的回调
   */
  dispatch(topic: string, data: any): void {
    const subscription = this.subscriptions.get(topic);

    if (!subscription) {
      if (this.debug) {
      }
      return;
    }

    if (subscription.callbacks.size === 0) {
      if (this.debug) {
      }
      return;
    }

    if (this.debug) {
    }

    // 执行所有回调
    subscription.callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        Track.log(
          "error",
          "SubscriptionManager",
          `执行回调出错 (主题: ${topic}):`,
          { cause: String(error) },
        );
      }
    });
  }

  /**
   * 分发消息到所有订阅(不区分主题)
   * 用于广播类消息
   */
  broadcast(data: any): void {
    if (this.debug) {
    }

    this.subscriptions.forEach((subscription) => {
      subscription.callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          Track.log(
            "error",
            "SubscriptionManager",
            `执行回调出错 (主题: ${subscription.topic}):`,
            { cause: String(error) },
          );
        }
      });
    });
  }

  /**
   * 重新发送所有订阅请求
   * 用于连接恢复后重新订阅
   */
  resubscribeAll(): void {
    if (!this.sendCallback) {
      Track.log("warn", "SubscriptionManager", "发送回调未设置,无法重新订阅");
      return;
    }

    if (this.debug) {
    }

    this.subscriptions.forEach((subscription) => {
      try {
        if (this.sendCallback) {
          this.sendCallback(subscription.topic);
          subscription.sent = true;

          if (this.debug) {
          }
        }
      } catch (error) {
        Track.log(
          "error",
          "SubscriptionManager",
          `重新订阅失败: ${subscription.topic}`,
          { cause: String(error) },
        );
        subscription.sent = false;
      }
    });
  }

  /**
   * 标记所有订阅为未发送状态
   */
  resetSentStatus(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.sent = false;
    });

    if (this.debug) {
    }
  }

  /**
   * 获取所有订阅的主题列表
   */
  getTopics(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  /**
   * 获取指定主题的回调数量
   */
  getCallbackCount(topic: string): number {
    const subscription = this.subscriptions.get(topic);
    return subscription ? subscription.callbacks.size : 0;
  }

  /**
   * 获取总的订阅数量
   */
  getSubscriptionCount(): number {
    return this.subscriptions.size;
  }

  /**
   * 获取总的回调数量
   */
  getTotalCallbackCount(): number {
    let count = 0;
    this.subscriptions.forEach((subscription) => {
      count += subscription.callbacks.size;
    });
    return count;
  }

  /**
   * 检查主题是否已订阅
   */
  hasSubscription(topic: string): boolean {
    return this.subscriptions.has(topic);
  }

  /**
   * 检查主题是否已发送订阅请求
   */
  isSubscriptionSent(topic: string): boolean {
    const subscription = this.subscriptions.get(topic);
    return subscription ? subscription.sent : false;
  }

  /**
   * 获取订阅信息(用于调试)
   */
  getSubscriptionInfo(): Array<{
    topic: string;
    callbackCount: number;
    subscribedAt: number;
    sent: boolean;
  }> {
    const info: Array<{
      topic: string;
      callbackCount: number;
      subscribedAt: number;
      sent: boolean;
    }> = [];

    this.subscriptions.forEach((subscription) => {
      info.push({
        topic: subscription.topic,
        callbackCount: subscription.callbacks.size,
        subscribedAt: subscription.subscribedAt,
        sent: subscription.sent,
      });
    });

    return info;
  }

  /**
   * 清理空订阅(没有回调的订阅)
   */
  cleanup(): void {
    const emptyTopics: string[] = [];

    this.subscriptions.forEach((subscription, topic) => {
      if (subscription.callbacks.size === 0) {
        emptyTopics.push(topic);
      }
    });

    emptyTopics.forEach((topic) => {
      this.subscriptions.delete(topic);
    });

    if (this.debug && emptyTopics.length > 0) {
    }
  }
}
